import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Bell, X, Check, MoreHorizontal, ChevronDown, Filter } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '../lib/queryClient';
import { updateAppBadge } from '../lib/notifications';
import { useIsMobile } from '@/hooks/use-mobile';

interface Notification {
  id: string;
  playerId: string;
  title: string;
  description: string;
  type: 'match' | 'club' | 'premium' | 'system' | 'achievement';
  isRead: boolean;
  createdAt: string;
  metadata?: {
    matchId?: string;
    clubId?: string;
    [key: string]: any;
  };
}

interface NotificationDropdownProps {
  playerId: string | null;
  onNavigateToMatches?: (filter?: "mine" | "clubs" | "all") => void;
  onNavigateToProfile?: (section: string) => void;
  onNavigateToClubs?: (clubId?: string) => void;
  onNavigateToPlayers?: () => void;
}

export default function NotificationDropdown({ playerId, onNavigateToMatches, onNavigateToProfile, onNavigateToClubs, onNavigateToPlayers }: NotificationDropdownProps) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState<string>('all');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();

  // Fetch notifications from API
  const { data: notifications = [], isLoading, refetch } = useQuery<Notification[]>({
    queryKey: ["/api/notifications"],
    queryFn: async () => {
      const response = await apiRequest("GET", "/api/notifications");
      return response.json();
    },
    enabled: !!playerId,
    refetchOnWindowFocus: true,
    staleTime: 30000, // 30 seconds
  });

  // Mark notification as read mutation
  const markAsReadMutation = useMutation({
    mutationFn: async (notificationId: string) => {
      const response = await apiRequest("PATCH", `/api/notifications/${notificationId}/read`);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/notifications"] });
    },
  });

  // Mark all as read mutation
  const markAllAsReadMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest("PATCH", "/api/notifications/mark-all-read");
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/notifications"] });
    },
  });

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setIsFilterOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Filter notifications based on selected filter
  const filteredNotifications = notifications.filter(notification => {
    if (selectedFilter === 'all') return true;
    if (selectedFilter === 'unread') return !notification.isRead;
    return notification.type === selectedFilter;
  });

  // Count unread notifications
  const unreadCount = notifications.filter(n => !n.isRead).length;

  // Update app badge when unread count changes
  useEffect(() => {
    if (playerId) {
      updateAppBadge(unreadCount).catch((error) => {
        console.error('[NotificationDropdown] Failed to update badge:', error);
      });
    }
  }, [unreadCount, playerId]);

  // Handle notification click
  const handleNotificationClick = (notification: Notification) => {
    if (!notification.isRead) {
      markAsReadMutation.mutate(notification.id);
    }

    // Special handling for match-related notifications - navigate to matches
    if (notification.type === 'match' && onNavigateToMatches) {
      if (notification.metadata?.action === 'validate') {
        onNavigateToMatches("clubs");
        setIsOpen(false);
      } else if (notification.title.includes('Invitation') ||
        notification.title.includes('Confirmation') ||
        notification.title.includes('Cancelled')) {
        onNavigateToMatches();
        setIsOpen(false);
      }
    }

    // Special handling for premium notifications - could navigate to profile/subscription page
    if (notification.type === 'premium') {
      // Premium notifications navigation
      if (onNavigateToProfile) {
        onNavigateToProfile("premium-section");
        setIsOpen(false);
      }
      console.log('Premium notification clicked:', notification.title);
    }

    // Special handling for club join requests - navigate to clubs tab and open edit dialog
    if ((notification.type === 'system' || notification.type === 'club') &&
      (notification.metadata?.type === 'club_join_request' || notification.title.toLowerCase().includes('want to join') || notification.title.toLowerCase().includes('rejoindre') || notification.title === 'Club Join Request') &&
      notification.metadata?.clubId) {
      if (onNavigateToClubs) {
        onNavigateToClubs(notification.metadata.clubId);
        setIsOpen(false);
      }
    }

    // FFT ranking updated - navigate to players tab
    if (notification.metadata?.type === 'fft_ranking_updated' && onNavigateToPlayers) {
      onNavigateToPlayers();
      setIsOpen(false);
    }
  };

  // Mark all as read
  const markAllAsRead = () => {
    markAllAsReadMutation.mutate();
  };

  // Get relative time
  const getRelativeTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));

    if (diffInMinutes < 1) return t('notifications.time.justNow');
    if (diffInMinutes < 60) return `${diffInMinutes}${t('notifications.time.m')} ${t('notifications.time.ago', { time: '' })}`;

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}${t('notifications.time.h')} ${t('notifications.time.ago', { time: '' })}`;

    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays}${t('notifications.time.d')} ${t('notifications.time.ago', { time: '' })}`;
  };

  // Helper to translate notification content
  const getNotificationContent = (notification: Notification) => {
    let title = notification.title;
    let description = notification.description;

    // Try to translate based on type and metadata
    if (notification.type === 'premium') {
      const type = notification.metadata?.notificationType;
      // TRICKY: premiumType is stored as 'monthly', 'yearly', 'lifetime' in metadata
      // We need to translate it for the UI
      const rawPremiumType = notification.metadata?.premiumType as string | undefined;
      const translatedPremiumType = rawPremiumType
        ? (rawPremiumType === 'monthly' ? t('profile.monthly') :
          rawPremiumType === 'yearly' ? t('profile.yearly') :
            rawPremiumType === 'lifetime' ? t('profile.lifetime') :
              rawPremiumType)
        : '';

      if (type === 'activation') {
        title = t('notifications.types.premiumActivated');
        description = t('notifications.types.premiumActivatedDesc', { premiumType: translatedPremiumType });
      } else if (type === 'expiry_warning') {
        title = t('notifications.types.premiumExpiring');
        description = t('notifications.types.premiumExpiringDesc', { premiumType: translatedPremiumType, days: notification.metadata?.daysLeft });
      } else if (type === 'expired') {
        title = t('notifications.types.premiumExpired');
        description = t('notifications.types.premiumExpiredDesc', { premiumType: translatedPremiumType });
      } else if (type === 'deactivation') {
        title = t('notifications.types.premiumDeactivated');
        description = t('notifications.types.premiumDeactivatedDesc', { premiumType: translatedPremiumType });
      }
    } else if (notification.type === 'match') {
      if (notification.title.includes('Invitation')) {
        title = t('notifications.types.matchInvitation');
        description = t('notifications.types.matchInvitationDesc', { name: notification.metadata?.creatorName || 'A player' });
      } else if (notification.title.includes('Confirmation')) {
        title = t('notifications.types.matchConfirmation');
        description = t('notifications.types.matchConfirmationDesc');
      } else if (notification.title.includes('Cancelled')) {
        title = t('notifications.types.matchCancelled');
        description = t('notifications.types.matchCancelledDesc');
      } else if (notification.title.includes('Club Match Validation') || notification.title.includes('Partner Match Validation')) {
        title = t('notifications.types.clubMatchValidation');
        description = t('notifications.types.clubMatchValidationDesc', { clubName: notification.metadata?.clubName || 'Club' });
      }
    } else if (notification.type === 'club') {
      if (notification.title.includes('Invitation')) {
        title = t('notifications.types.clubInvitation');
        description = t('notifications.types.clubInvitationDesc', { name: notification.metadata?.inviterName || 'Someone', clubName: notification.metadata?.clubName || 'a club' });
      } else if (notification.title === 'Club Join Request' || notification.metadata?.type === 'club_join_request') {
        title = t('notifications.types.clubJoinRequest');
        description = t('notifications.types.clubJoinRequestDesc', { name: notification.metadata?.joinerName || 'A player', clubName: notification.metadata?.clubName || 'your club' });
      }
    }
    return { title, description };
  };

  // Filter options
  const filterOptions = [
    { value: 'all', label: t('notifications.filters.all') },
    { value: 'unread', label: t('notifications.filters.unread') },
    { value: 'match', label: t('notifications.filters.matches') },
    { value: 'club', label: t('notifications.filters.clubs') },
    { value: 'premium', label: t('notifications.filters.premium') },
    { value: 'system', label: t('notifications.filters.system') },
    { value: 'achievement', label: t('notifications.filters.achievements') },
  ];

  const selectedFilterLabel = filterOptions.find(opt => opt.value === selectedFilter)?.label || t('notifications.filters.all');

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Icon with Badge */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative w-10 h-10 bg-white bg-opacity-20 rounded-full flex items-center justify-center hover:bg-opacity-30 transition-colors dark-btn-active"
        data-testid="button-notifications"
        title="Notifications"
      >
        <Bell className="text-sm" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-medium">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className={`absolute ${isMobile ? 'right-0' : 'left-0'} mt-2 w-80 max-w-[calc(100vw-2rem)] bg-card rounded-lg shadow-lg border border-border z-50`}>
          {/* Header */}
          <div className="px-4 py-3 border-b border-border flex items-center justify-between">
            <h3 className="text-sm font-semibold text-foreground">{t('notifications.title')}</h3>
            <div className="flex items-center space-x-2">
              {unreadCount > 0 && (
                <button
                  onClick={markAllAsRead}
                  className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                  disabled={markAllAsReadMutation.isPending}
                >
                  {markAllAsReadMutation.isPending ? t('notifications.marking') : t('notifications.markAllRead')}
                </button>
              )}
              <button
                onClick={() => setIsOpen(false)}
                className="text-muted-foreground hover:text-foreground"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Filter */}
          <div className="px-4 py-2 border-b border-border">
            <div className="relative">
              <button
                onClick={() => setIsFilterOpen(!isFilterOpen)}
                className="flex items-center space-x-2 text-sm text-muted-foreground hover:text-foreground"
              >
                <Filter className="w-4 h-4" />
                <span>{selectedFilterLabel}</span>
                <ChevronDown className="w-3 h-3" />
              </button>

              {isFilterOpen && (
                <div className="absolute top-full left-0 mt-1 w-32 bg-card border border-border rounded-md shadow-lg z-10">
                  {filterOptions.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => {
                        setSelectedFilter(option.value);
                        setIsFilterOpen(false);
                      }}
                      className={`w-full px-3 py-2 text-left text-sm hover:bg-muted ${selectedFilter === option.value ? 'bg-blue-50 dark:bg-blue-950/30 text-blue-600 dark:text-blue-400' : 'text-muted-foreground'
                        }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Unread Summary */}
          {unreadCount > 0 && (
            <div className="px-4 py-2 bg-blue-50 dark:bg-blue-950/30 border-b border-border">
              <p className="text-xs text-blue-600 dark:text-blue-400 font-medium">
                {t('notifications.unreadSummary_plural', { count: unreadCount })}
              </p>
            </div>
          )}

          {/* Notifications List */}
          <div className="max-h-96 overflow-y-auto">
            {isLoading ? (
              <div className="px-4 py-8 text-center text-muted-foreground">
                <div className="animate-spin w-6 h-6 mx-auto mb-2 border-2 border-muted border-t-padel-green rounded-full"></div>
                <p>{t('common.loading')}</p>
              </div>
            ) : filteredNotifications.length === 0 ? (
              <div className="px-4 py-8 text-center text-muted-foreground">
                <Bell className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                <p className="text-sm">{t('notifications.empty')}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {selectedFilter === 'unread' ? t('notifications.caughtUp') : t('notifications.youWillSeeUpdates')}
                </p>
              </div>
            ) : (
              filteredNotifications.map((notification) => {
                const content = getNotificationContent(notification);
                return (
                  <div
                    key={notification.id}
                    className={`px-4 py-3 border-b border-border hover:bg-muted transition-colors cursor-pointer ${!notification.isRead ? 'bg-blue-50 dark:bg-blue-950/30' : ''
                      } ${notification.type === 'match' && (notification.title.includes('Invitation') || notification.title.includes('Confirmation') || notification.title.includes('Cancelled'))
                        ? 'hover:bg-blue-50 dark:hover:bg-blue-950/30 border-l-4 border-l-blue-400'
                        : notification.type === 'premium'
                          ? 'hover:bg-yellow-50 dark:hover:bg-yellow-950/30 border-l-4 border-l-yellow-400'
                          : ''
                      }`}
                    onClick={() => handleNotificationClick(notification)}
                  >
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0">
                        {!notification.isRead && (
                          <div className="w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <h4 className={`text-sm font-medium ${!notification.isRead ? 'text-foreground' : 'text-muted-foreground'
                            }`}>
                            {content.title}
                          </h4>
                          <span className="text-xs text-muted-foreground ml-2 flex-shrink-0">
                            {getRelativeTime(notification.createdAt)}
                          </span>
                        </div>
                        <p className={`text-xs mt-1 ${!notification.isRead ? 'text-muted-foreground' : 'text-muted-foreground'
                          }`}>
                          {content.description}
                        </p>

                        {/* Special styling for match invitations and confirmations */}
                        {notification.type === 'match' && (notification.title.includes('Invitation') || notification.title.includes('Confirmation')) && (
                          <div className="mt-2 text-xs text-blue-600 font-medium">
                            {t('notifications.actions.clickToView')}
                          </div>
                        )}

                        {/* Special styling for premium notifications */}
                        {notification.type === 'premium' && (
                          <div className="mt-2 text-xs text-yellow-600 font-medium">
                            {t('notifications.actions.premiumUpdate')}
                          </div>
                        )}

                        {/* Special styling for club join requests */}
                        {(notification.type === 'system' || notification.type === 'club') &&
                          (notification.metadata?.type === 'club_join_request' || notification.title === 'Club Join Request') && (
                            <div className="mt-2 text-xs text-green-600 font-medium">
                              {t('notifications.actions.manageClub')}
                            </div>
                          )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Footer */}
          <div className="px-4 py-3 border-t border-border bg-muted rounded-b-lg">
            <div className="flex items-center justify-between">
              <p className="text-xs text-muted-foreground">
                {filteredNotifications.length} notification{filteredNotifications.length !== 1 ? 's' : ''}
              </p>
              <button
                onClick={() => refetch()}
                className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                disabled={isLoading}
              >
                {isLoading ? t('notifications.refreshing') : t('notifications.refresh')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
